import { useApiClient } from '../../api/useApiClient';

/**
 * Servicio para gestión de ACLs (Access Control Lists)
 * Encapsula todas las operaciones de administración de ACLs
 */
class AclService {
    constructor(apiClient) {
        this.apiClient = apiClient;
    }

    /**
     * Listar todas las ACLs
     * @returns {Promise} Response con lista de ACLs
     */
    async listAcls() {
        return this.apiClient.get('/api/v1/acls');
    }

    /**
     * Crear una nueva ACL
     * @param {Object} aclData - Datos de la ACL a crear
     * @returns {Promise} Response con la ACL creada
     */
    async createAcl(aclData) {
        return this.apiClient.post('/api/v1/acls', aclData);
    }

    /**
     * Actualizar una ACL existente
     * @param {string} id - ID de la ACL
     * @param {Object} aclData - Datos actualizados de la ACL
     * @returns {Promise} Response con la ACL actualizada
     */
    async updateAcl(id, aclData) {
        return this.apiClient.put(`/api/v1/acls/${id}`, aclData);
    }

    /**
     * Eliminar una ACL
     * @param {string} id - ID de la ACL a eliminar
     * @returns {Promise} Response con resultado de la eliminación
     */
    async deleteAcl(id) {
        return this.apiClient.delete(`/api/v1/acls/${id}`);
    }

    /**
     * Admin de ACLs: lista TODAS las series para que el admin pueda crear/editar ACLs
     * para cualquier serie, incluso las que su grupo no tiene. Requiere ADMIN.
     */
    async getTiposDocumentos() {
        return this.apiClient.get('/api/v1/series?scope=all');
    }

    /**
     * Lista todos los folders del árbol — usado para construir el árbol
     * contextual en la nueva vista de gestión de ACLs.
     */
    async getFolders() {
        return this.apiClient.get('/api/v1/admin/folders');
    }

    /**
     * Audita las ACLs aplicables a un nodo: locales + heredadas + efectivas.
     * Backend resuelve la cadena de ancestros y aplica semántica A.2.
     */
    async getAclAudit(nodeId) {
        return this.apiClient.get(`/api/v1/acls/audit/${nodeId}`);
    }

    /**
     * Estima series descendientes afectadas por cambio/borrado de una ACL en
     * este nodo. Usado para el warning previo al submit.
     */
    async getAclImpact(nodeId) {
        return this.apiClient.get(`/api/v1/acls/impact/${nodeId}`);
    }
}

/**
 * Hook personalizado para usar el servicio de ACLs
 * @returns {AclService} Instancia del servicio de ACLs
 */
export const useAclService = () => {
    const apiClient = useApiClient();
    return new AclService(apiClient);
};

export default AclService;
